import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { PaginatorState } from 'primeng/paginator';
import { finalize } from 'rxjs/operators';
import { LotItemRelocationDto } from '../../../models/inventory/lot-item-relocation';
import { LotItemRelocationProxyService } from '../../../proxies/inventory/lot-item-relocation.proxy.service';

@Component({
  selector: 'app-lot-item-relocation',
  templateUrl: './lot-item-relocation.component.html',
  styleUrl: './lot-item-relocation.component.scss',
  standalone: false,
})
export class LotItemRelocationComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly relocationProxyService = inject(LotItemRelocationProxyService);

  isLoading = signal(0);
  relocations = signal<LotItemRelocationDto[]>([]);
  totalRecords = signal(0);
  currentPage = signal(1);
  pageSize = signal(25);

  firstRecord = computed(() => (this.totalRecords() === 0 ? 0 : (this.currentPage() - 1) * this.pageSize() + 1));
  lastRecord = computed(() => Math.min(this.currentPage() * this.pageSize(), this.totalRecords()));

  ngOnInit(): void {
    this.search();
  }

  private startLoading(): void {
    this.isLoading.update((value) => value + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((value) => value - 1);
  }

  search(): void {
    this.startLoading();
    this.relocationProxyService
      .search({}, this.currentPage(), this.pageSize())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.relocations.set(response?.data?.items ?? []);
          this.totalRecords.set(response?.data?.totalCount ?? 0);
        },
        error: () => {
          this.relocations.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.search();
  }

  getRelocation(row: unknown): LotItemRelocationDto {
    return row as LotItemRelocationDto;
  }

  getLotItemNumbers(relocation: LotItemRelocationDto): string {
    const numbers = relocation.lines.map((line) => line.lotItemNumber);
    if (numbers.length <= 3) return numbers.join(', ');
    return `${numbers[0]} … ${numbers[numbers.length - 1]}`;
  }

  getFromLocations(relocation: LotItemRelocationDto): string {
    const codes = [...new Set(relocation.lines.map((line) => line.fromLocationCode).filter(Boolean))];
    if (codes.length <= 3) return codes.join(', ');
    return `${codes.slice(0, 3).join(', ')} +${codes.length - 3}`;
  }

  getMovedBy(relocation: LotItemRelocationDto): string {
    const lastEntry = relocation.statusHistory?.[relocation.statusHistory.length - 1];
    const user = lastEntry?.user;
    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
    return fullName || user?.username || relocation.createdBy || '';
  }

  redirectToDetails(id?: string): void {
    this.router.navigate(['/lot-item-relocation-details'], { queryParams: { id: id ?? 'new' } });
  }
}
